/**
 * React Query Configuration
 * 
 * Provides optimized caching, refetching, and error handling
 * for all API calls in the application.
 */

import { QueryClient, DefaultOptions } from '@tanstack/react-query';

const queryConfig: DefaultOptions = {
  queries: {
    // Cache de 5 minutos por padrão (dados permanecem "frescos")
    staleTime: 5 * 60 * 1000,
    
    // Manter cache por 10 minutos (dados ficam disponíveis mesmo "stale")
    gcTime: 10 * 60 * 1000,
    
    // Retry inteligente em caso de falha
    retry: (failureCount, error: any) => {
      // Não retry em erros de autenticação (401, 403)
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      
      // Não retry em erros de validação (400, 422)
      if (error?.status === 400 || error?.status === 422) {
        return false;
      }
      
      // Não retry em erros "not found" (404)
      if (error?.status === 404) {
        return false;
      }
      
      // Retry até 3 vezes em erros de rede (500, timeout, etc)
      return failureCount < 3;
    },
    
    // Delay exponencial entre retries (1s, 2s, 4s, max 30s)
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    
    // Refetch ao voltar para a janela (detecta dados desatualizados)
    refetchOnWindowFocus: true,
    
    // Refetch quando reconecta (recupera de offline)
    refetchOnReconnect: true,
    
    // Refetch quando volta a ser visível
    refetchOnMount: true,
  },
  mutations: {
    // Não retry mutations por padrão (evita duplicação)
    // Cada mutation pode configurar retry individualmente se necessário
    retry: false,
    
    // Network errors podem ser retried uma vez
    onError: (error: any) => {
      console.error('[Mutation Error]', error);
    },
  },
};

export const queryClient = new QueryClient({
  defaultOptions: queryConfig,
});

/**
 * Query Keys Factory
 * 
 * Centraliza e padroniza as keys usadas no React Query
 */
export const queryKeys = {
  // Auth
  auth: ['auth'] as const,
  user: ['user'] as const,
  
  // Companies
  companies: ['companies'] as const,
  company: (id: string) => ['company', id] as const,
  
  // Transactions
  transactions: (companyId: string) => ['transactions', companyId] as const,
  transaction: (id: string) => ['transaction', id] as const,
  transactionsByPeriod: (companyId: string, startDate: string, endDate: string) => 
    ['transactions', companyId, 'period', startDate, endDate] as const,
  
  // Reports
  reports: (companyId: string) => ['reports', companyId] as const,
  reportData: (companyId: string, reportType: string, period: string) => 
    ['report', companyId, reportType, period] as const,
  
  // Budget
  budgets: (companyId: string) => ['budgets', companyId] as const,
  budget: (id: string) => ['budget', id] as const,
  budgetComparison: (companyId: string, year: number) => 
    ['budget', companyId, 'comparison', year] as const,
  
  // Scenarios
  scenarios: (companyId: string) => ['scenarios', companyId] as const,
  scenario: (id: string) => ['scenario', id] as const,
  scenarioProjection: (id: string) => ['scenario', id, 'projection'] as const,
  
  // Sharing
  sharing: (companyId: string) => ['sharing', companyId] as const,
  invites: (companyId: string) => ['invites', companyId] as const,
} as const;
